import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, KpiCard } from "@/components/shared";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import { ArrowDownToLine, ArrowUpFromLine, Users, AlertTriangle } from "lucide-react";

const COLORS = ["#229ED9", "#10b981", "#f59e0b", "#a78bfa", "#f43f5e", "#38bdf8", "#64748b", "#e11d48"];

export default function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Skeleton className="h-96 w-full" />;
  const t = d.totals;
  return (
    <div className="space-y-6">
      <PageHeader title="Analytics" desc="Captured application metrics across all bots (real data only)." testId="analytics-header" />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard label="Incoming" value={t.incoming} icon={ArrowDownToLine} tone="sky" testId="kpi-incoming" />
        <KpiCard label="Outgoing" value={t.outgoing} icon={ArrowUpFromLine} tone="emerald" testId="kpi-outgoing" />
        <KpiCard label="Users" value={t.users} icon={Users} tone="violet" testId="kpi-users" />
        <KpiCard label="Errors" value={t.errors} icon={AlertTriangle} tone="rose" testId="kpi-errors" />
        <KpiCard label="Delivery" value={`${t.delivery_rate}%`} tone="emerald" testId="kpi-delivery" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-lg p-5">
          <h3 className="font-semibold mb-3">Messages · last 14 days</h3>
          <ResponsiveContainer width="100%" height={260}><AreaChart data={d.series}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" /><XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" /><YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12, color: "#fff" }} />
            <Area type="monotone" dataKey="incoming" stroke="#229ED9" fill="#229ED9" fillOpacity={0.15} strokeWidth={2} />
            <Area type="monotone" dataKey="outgoing" stroke="#10b981" fill="transparent" strokeWidth={2} />
          </AreaChart></ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-lg p-5">
          <h3 className="font-semibold mb-3">By language</h3>
          {d.languages.length === 0 ? <div className="text-sm text-muted-foreground py-10 text-center">No data yet.</div> : (
            <ResponsiveContainer width="100%" height={220}><PieChart>
              <Pie data={d.languages} dataKey="count" nameKey="language" outerRadius={80}>{d.languages.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie>
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12, color: "#fff" }} />
            </PieChart></ResponsiveContainer>
          )}
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-5">
        <h3 className="font-semibold mb-3">Top bots by incoming updates</h3>
        {d.top_bots.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">No data yet.</div> : (
          <ResponsiveContainer width="100%" height={220}><BarChart data={d.top_bots}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" /><XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" /><YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12, color: "#fff" }} cursor={{ fill: "rgba(148,163,184,0.1)" }} />
            <Bar dataKey="count" fill="#229ED9" radius={[4, 4, 0, 0]} />
          </BarChart></ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
